import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { AnyObject } from 'final-form';
import React, { ReactNode } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Category, ProductHierarchyLevel } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { APIErrorNotification, WoltModal } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';

import RestockingRuleFields, { RestockingRuleFormValues } from './RestockingRuleFields';
import { restockingRuleFormValidationSchema } from './validations';

type Props = {
  categories: Category[];
  productFamilies: ProductHierarchyLevel[];
  language: Language;
  isOpen: boolean;
  venueId: string;
  onClose: () => void;
  title: ReactNode;
  onSubmit: (values: RestockingRuleFormValues) => Promise<AnyObject | undefined> | undefined;
  initialFormValues: RestockingRuleFormValues;
};

const Root = styled(WoltModal)`
  border-radius: 8px;
  max-width: 640px;
  width: 100%;
  background: ${cbSemanticCssVars.surfaceMain};
`;

const CancelButton = styled(Button)`
  margin-inline-start: auto;
  margin-inline-end: 16px;
`;

const RestockingRuleModal = ({
  isOpen,
  onClose,
  categories,
  language,
  productFamilies,
  onSubmit,
  title,
  initialFormValues,
}: Props) => {
  const intl = useIntl();

  return (
    <Root isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>{title}</ModalHeader>
      <Form<RestockingRuleFormValues>
        initialValues={initialFormValues}
        onSubmit={onSubmit}
        validate={validateYupSchema(restockingRuleFormValidationSchema)}
      >
        {({ handleSubmit, hasValidationErrors, submitting, dirty, submitError }) => (
          <>
            <RestockingRuleFields categories={categories} productFamilies={productFamilies} language={language} />
            {submitError && (
              <ModalContent>
                <APIErrorNotification error={submitError} />
              </ModalContent>
            )}
            <ModalFooter>
              <CancelButton size="small" variant="secondary" disabled={submitting} onClick={onClose}>
                {intl.formatMessage({ id: 'menu.restockingRules.ruleForm.cancel' })}
              </CancelButton>
              <Button
                size="small"
                variant="primary"
                onClick={handleSubmit}
                loading={submitting}
                disabled={hasValidationErrors || submitting || !dirty}
              >
                {intl.formatMessage({ id: 'menu.restockingRules.ruleForm.save' })}
              </Button>
            </ModalFooter>
          </>
        )}
      </Form>
    </Root>
  );
};

export default RestockingRuleModal;
